import { FC, ReactNode } from "react";
import { observer } from "mobx-react";
import { Box, VBox } from "@/components-v2/Box";
import { IconButton } from "@/components-v2/Button";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { Icon } from "@/components-v2/Icon";
import { DCEvent } from "@/events/eventsDomain";
import { useDispatch, useTranslation } from "@/stores/RootStoreContext";
import {
  AccountUserGroup,
  accountUserGroupLabel,
  addableGroups,
  memberCountLabel,
  visibleGroups,
} from "./accountVisibilityDomain";

export const VisibleToEditor: FC<{
  groups: AccountUserGroup[];
  visibleTo: string[];
  onAdd: (grantee: string) => DCEvent[];
  onRemove: (grantee: string) => DCEvent[];
}> = observer(({ groups, visibleTo, onAdd, onRemove }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const addable = addableGroups(groups, visibleTo);
  return (
    <VBox gap={"sm"}>
      <b>{t("enterprise-account.visibility.visible-to")}</b>
      {visibleGroups(groups, visibleTo).map((group) => (
        <ListRow
          key={group.grantee}
          icon={"users"}
          label={t(accountUserGroupLabel(group))}
          caption={t(memberCountLabel(group))}
          onRemove={() => dispatch(onRemove(group.grantee))}
        />
      ))}
      <Box>
        <DropdownMenu
          button={{
            type: "link",
            icon: "plus",
            label: { key: "enterprise-account.visibility.add-user-group" },
          }}
          disabled={addable.length === 0}
          alignment={"start"}
          items={addable.map((group) => ({
            type: "item",
            label: accountUserGroupLabel(group),
            categoryTag: memberCountLabel(group),
            onSelect: onAdd(group.grantee),
          }))}
        />
      </Box>
    </VBox>
  );
});

export const ListRow: FC<{
  icon?: "users";
  dragHandle?: ReactNode;
  label: string;
  caption?: string;
  onRemove: () => void;
}> = ({ icon, dragHandle, label, caption, onRemove }) => (
  <Box
    border
    rounded
    alignItems={"center"}
    gap={"sm"}
    paddingX={"md"}
    paddingY={"sm"}
    bg={"default"}
  >
    {dragHandle}
    {icon && <Icon name={icon} />}
    <Box grow>{label}</Box>
    {caption && (
      <Box fg={"subtle"} textSize={"sm"}>
        {caption}
      </Box>
    )}
    <IconButton icon={"x"} tooltip={{ key: "remove" }} color={"gray"} onClick={onRemove} />
  </Box>
);
